'use strict';

import ServersConfig from '../../../core/config/servers.json';

const ALL_ORGS_LOCAL_STORAGE_KEY = 'Organizations-All';
const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
const SELECTED_LOCATION_LOCAL_STORAGE_KEY = 'Selected-Location';

const TRENDS_HISTORY_AGGREGATIONS = {
  none: 0,
  hour: 1,
  day: 2,
  month: 3,
  week: 4
};

export default angular
  .module('main.locations.edit.dataProvider', [])
  .factory('EditLocationDataProvider', function(
    $q,
    $log,
    $rootScope,
    $state,
    $filter,
    utils,
    UsersService,
    Resource,
    ResourceGroup,
    OrganizationService,
    LocationsService,
    PermissionsService,
    BotsService,
    DevicesService,
    CacheService,
    RulesService,
    SubscriptionsService,
    MonitoringService,
    QuestionsService,
    config,
    CloudConfigService,
    TagsService
  ) {
    const $t = $filter('translate');
    const orderBy = $filter('orderBy');
    const GATEWAY_DEVICE_CATEGORIES = config.getConfigByKey('gatewayDeviceCategories');

    class EditLocationDataProvider {

      // #region Public fields

      // #region Resources

      /**
       * Available organizations
       * @type {Resource}
       */
      availableOrganizationsResource = new Resource(
        () => {
          // TODO move get all orgs stuff to the OrganizationService
          return utils.getStorageItem(ALL_ORGS_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Current organization
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(
        () => {
          // TODO move get/set current org stuff to the OrganizationService
          return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Current user (admin currently logged in)
       * @type {Resource}
       */
      currentUserResource = new Resource(
        () => {
          return UsersService.getCurrentUserInfo();
        }
      );

      /**
       * Location ID
       * @type {Resource}
       */
      locationIdResource = new Resource(
        () => {
          // TODO REMOVE LOCAL STORAGE THING
          let result = $state.params.locationId || utils.getStorageItem(SELECTED_LOCATION_LOCAL_STORAGE_KEY)?.id;
          return parseInt(result);
        }
      );

      /**
       * Organization ID of current location organization
       * @type {Resource}
       */
      locationOrganizationIdResource = new Resource(
        () => {
          return this.locationResource?.value?.organizationId || void 0;
          // Removed as we need to open location from direct URL, see CC-862
          // this.currentOrganizationResource.value.id;
        },
        [
          this.currentOrganizationResource
        ]
      );

      /**
       * Relative locations resource
       * @type {Resource}
       */
      locationRelativesResource = new Resource(
        () => {
          return this.locationRelativesResource || [];
        },
        [
          this.locationIdResource
        ]
      );

      /**
       * Location Resource
       * @type {Resource}
       */
      locationResource = new Resource(() => {
          return LocationsService.getLocationInfo(this.locationOrganizationIdResource.value, this.locationIdResource.value)
            .then(data => {
              let location = data.locations
                ? data.locations.find(l => l.id === this.locationIdResource.value)
                : undefined;
              if (!location) {
                return $q.reject({ resultCode: 8 });
              }
              utils.setStorageItem(SELECTED_LOCATION_LOCAL_STORAGE_KEY, location);

              // Force update other resources
              this.locationOrganizationIdResource.value = location.organizationId || this.currentOrganizationResource.value.id;
              this.locationRelativesResource.value = data.locations
                ? data.locations.filter(l => l.id !== this.locationIdResource.value)
                : [];

              return location;
            });
        },
        [
          this.currentOrganizationResource,
          this.locationOrganizationIdResource,
          this.locationIdResource,
          this.locationRelativesResource,
        ],
        $state.params.location || utils.getStorageItem(SELECTED_LOCATION_LOCAL_STORAGE_KEY)
      );

      /**
       * Location organization
       * @type {Resource}
       */
      locationOrganizationResource = new Resource(
        () => {
          return this.availableOrganizationsResource.value
            ?.find(o => o.id === this.locationResource.value.organizationId);
        }, [
          this.availableOrganizationsResource,
          this.locationResource
        ]
      );

      /**
       * Location organization properties (organizational objects)
       * @type {Resource}
       */
      locationOrganizationPropertiesResource = new Resource(() => {
        return OrganizationService.getObjectsAndProperties(this.locationOrganizationIdResource.value)
          .then(result => result?.organizationObjects || []);
      }, [
        this.locationOrganizationIdResource
      ]);

      /**
       * True if location organization has feature "G" (Groups) enabled
       * @type {Resource}
       */
      locationOrganizationHasFeatureGResource = new Resource(() => {
        return utils.hasFeature(this.locationOrganizationResource.value, 'g');
      }, [
        this.locationOrganizationResource
      ]);

      /**
       * True if location organization has feature "P" (Priorities) enabled
       * @type {Resource}
       */
      locationOrganizationHasFeaturePResource = new Resource(() => {
        return utils.hasFeature(this.locationOrganizationResource.value, 'p');
      }, [
        this.locationOrganizationResource
      ]);

      /**
       * Location timezone ID
       * @type {Resource}
       */
      locationTimeZoneIdResource = new Resource(() => {
        if (this.locationResource.value.timezone
          && this.locationResource.value.timezone.id
          && !!moment.tz.zone(this.locationResource.value.timezone.id)) {
          return this.locationResource.value.timezone.id;
        }
        return moment.tz.guess() || 'GMT';
      }, [
        this.locationResource
      ]);

      /**
       * Location timezone Offset (applicable for `date` angular filter)
       * @type {Resource}
       */
      locationTimeZoneOffsetResource = new Resource(() => {
        return moment().tz(this.locationTimeZoneIdResource.value).format('Z');
      }, [
        this.locationTimeZoneIdResource
      ]);

      /**
       * List of countries available for selection
       * @type {Resource}
       */
      countriesResource = new Resource(() => {
        return LocationsService.getCountries()
          .then(function(data) {
            return data.countries;
          });
      }, []);

      /**
       * Resource for checking if current user is allowed to edit current location
       * @type {Resource}
       */
      canIEditLocationResource = new Resource(
        () => {
          return PermissionsService.canIEditLocation(this.locationResource.value);
        },
        [
          this.locationResource
        ]
      );

      /**
       * Resources that we need for every tab
       * @type {ResourceGroup}
       */
      mainResourceGroup = new ResourceGroup([
        this.availableOrganizationsResource,
        this.currentOrganizationResource,
        this.currentUserResource,
        this.locationResource
      ]);

      /**
       * All location users
       * @type {Resource}
       */
      locationUsersResource = new Resource(
        () => {
          return LocationsService.getLocationUsers(this.locationIdResource.value)
            .then(data => {
              return data?.users || [];
            });
        },
        [
          this.locationIdResource
        ]
      );

      /**
       * Location users with admin rights on current location
       * @type {Resource}
       */
      locationAdminUsersResource = new Resource(
        () => {
          let allUsers = this.locationUsersResource.value || [];
          return allUsers.filter(user => user.locationAccess === 30);
        },
        [
          this.locationUsersResource
        ]
      );

      /**
       * Other location users without admin right on current location
       * @type {Resource}
       */
      locationOtherUsersResource = new Resource(
        () => {
          let allUsers = this.locationUsersResource.value || [];
          return allUsers.filter(user => user.locationAccess !== 30);
        },
        [
          this.locationUsersResource
        ]
      );

      /**
       * Location bots summary (microservices and data stream addresses for specified location)
       * @type {Resource}
       */
      locationBotSummaryResource = new Resource(
        () => {
          // Skip any bots-dependent resources for device-only location
          if (this.locationResource && this.locationResource.value?.subType === 1) {
            return $q.resolve(undefined);
          }

          return BotsService.getBotSummary(this.locationIdResource.value);
        },
        [
          this.locationIdResource
        ]
      );

      // #region Location States Resources

      tasksLocationStateResource = new Resource(() => {
        const stateName = 'tasks';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.tasksLocationStateResource.value = locationState;
          this.tasksLocationStateResource.forceReInitDependentsRecursively();
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      behaviorsLocationStateResource = new Resource(() => {
        const stateName = 'behaviors';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.behaviorsLocationStateResource.value = locationState;
          this.behaviorsLocationStateResource.forceReInitDependentsRecursively();
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      servicesLocationStateResource = new Resource(() => {
        const stateName = 'services';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.servicesLocationStateResource.value = locationState;
          this.servicesLocationStateResource.forceReInitDependentsRecursively();
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      dashboardHeaderLocationStateResource = new Resource(() => {
        const stateName = 'dashboard_header';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.dashboardHeaderLocationStateResource.value = locationState;
          this.dashboardHeaderLocationStateResource.forceReInitDependentsRecursively();
          this.locationHeaderResource.init(true);
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      nowLocationStateResource = new Resource(() => {
        const stateName = 'now';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.nowLocationStateResource.value = locationState;
          this.nowLocationStateResource.forceReInitDependentsRecursively();
          this.locationCardsResource.init(true);
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      trendsLocationStateResource = new Resource(() => {
        const stateName = 'trends_metadata';
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      trendsCategoryLocationStateResource = new Resource(() => {
        const stateName = 'trends_category';
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      occupancyLocationStateResource = new Resource(() => {
        const stateName = 'occupancy_overview';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.occupancyLocationStateResource.value = locationState;
          this.occupancyLocationStateResource.forceReInitDependentsRecursively();
          this.locationOccupancyResource.init(true);
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      // #endregion

      /**
       * Location tasks.
       * @type {Resource}
       */
      locationTasksResource = new Resource(() => {
          return Object.entries(this.tasksLocationStateResource.value || {})
            .map(([key, value]) => ({
              ...value,
              id: key
            }));
        },
        [
          this.locationIdResource,
          this.tasksLocationStateResource
        ]
      );

      /**
       * List of users who is allowed to see/create tasks on current location
       * @type {Resource}
       */
      locationTaskUsersResource = new Resource(() => {
        return (this.locationUsersResource.value || []).slice(0);
      }, [
        this.locationUsersResource
      ]);

      /**
       * Check if "tasks" bot is available and we are able to edit location tasks
       * @type {Resource}
       */
      isLocationTasksEditableResource = new Resource(
        () => {
          return this.locationBotSummaryResource.value?.microServices?.some(m => m === 'tasks') || false;
        },
        [
          this.locationBotSummaryResource
        ]
      );

      /**
       * Location highlights.
       * @type {Resource}
       */
      locationHighlightsResource = new Resource(() => {
        if (!utils.hasFeature(this.locationOrganizationResource.value, 'h')) {
          return $q.resolve([]);
        }

        return $q.all([
          this.getLocationHighlightsMetadata(),
          this.highlightsLocationStateResource.init(),
        ])
          .then(([metadata, context]) => {
            const locationTags = this.locationResource.value.tags || [];
            const badges = [];

            if (!metadata) {
              return badges;
            }

            if (!metadata.highlights?.length && !metadata.indicators?.length) {
              return badges;
            }

            metadata.highlights = metadata.highlights || [];
            metadata.indicators = metadata.indicators || [];

            metadata.highlights.forEach(highlight => {
              // Looking for max priority over all available tags
              let maxPriority = undefined;

              (highlight.tags || []).forEach(highlightTag => {
                const locationHighlightedTags =
                  locationTags.filter(tag => tag.tag === highlightTag) || [];

                locationHighlightedTags.forEach(tag => {
                  if (tag.priority != null && (maxPriority == null || tag.priority > maxPriority)) {
                    maxPriority = tag.priority;
                  }
                });
              });

              const badge = {
                id: highlight.id,
                comment: context?.elements?.find(el => el.id === highlight.id)?.comment,
                color: utils.getHighlightColor(highlight.color, maxPriority),
                icon: highlight.icon,
                iconFont: highlight.iconFont,
                mlDesc: highlight.mlDesc,
              };

              badges.push(badge);
            });

            metadata.indicators.forEach(indicator => {
              let icon = undefined;
              let iconFont = undefined;
              let mlDesc = undefined;
              let color = undefined;
              let priority = undefined;

              indicator.options.forEach(option => {
                let maxPriority = undefined;

                (option.tags || []).forEach(highlightTag => {
                  const locationHighlightedTags =
                    locationTags.filter(tag => tag.tag === highlightTag) || [];

                  locationHighlightedTags.forEach(tag => {
                    if (tag.priority != null && (maxPriority == null || tag.priority > maxPriority)) {
                      maxPriority = tag.priority;
                    }
                  });
                });

                if (maxPriority != null) {
                  icon = option.icon;
                  iconFont = option.iconFont;
                  mlDesc = option.mlDesc;
                  color = option.color
                    ? utils.getHighlightColor(option.color, maxPriority)
                    : undefined;
                  priority = maxPriority;
                }
              });

              const badge = {
                id: indicator.id,
                comment: context?.elements?.find(el => el.id === indicator.id)?.comment,
                color: color || utils.getHighlightColor(indicator.color, priority),
                icon: icon || indicator.icon,
                iconFont: iconFont || indicator.iconFont,
                mlDesc: mlDesc || indicator.mlDesc,
              };

              badges.push(badge);
            });

            return badges;
          });
      },
      [
        this.locationOrganizationResource,
        this.locationResource,
      ]);

      highlightsLocationStateResource = new Resource(() => {
        const stateName = 'location_highlights';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.locationHighlightsResource.forceReInitDependentsRecursively();
          this.locationHighlightsResource.init(true);
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value);
      }, [
        this.locationIdResource
      ]);

      /**
       * Current location groups (grouping tags)
       */
      locationGroupsResource = new Resource(() => {
        return (this.locationResource.value.tags || [])
          .filter(tag => tag.category === 2); // Grouping tags
      }, [
        this.locationResource
      ]);

      /**
       * Groups (grouping tags) available for location
       * @type {Resource}
       */
      availableGroupsResource = new Resource(() => {
        if (!utils.hasFeature(this.locationOrganizationResource.value, 'g')) {
          return $q.resolve([]);
        }
        let tagType = 2; // Location tags
        let tagCategory = 2; // Grouping tags

        TagsService.getPopularTags.clearEntireCache();
        return TagsService.getPopularTags(this.locationResource.value.organizationId, tagType, tagCategory, 50)
          .then(data => {
            return data?.tags || [];
          });
      }, [
        this.locationResource,
        this.locationOrganizationResource
      ]);

      /**
       * Device types (it's not related to particular location)
       * @type {Resource}
       */
      deviceTypesResource = new Resource(() => {
        return DevicesService.getDeviceTypes()
          .then((data) => data?.deviceTypes || []);
      }, []);

      /**
       * Location devices
       * @type {Resource}
       */
      locationDevicesResource = new Resource(() => {
        return $q.all([
          DevicesService.getLocationDevices(this.locationIdResource.value, true, true),
          this.deviceTypesResource.init() // it's here to make it load in parallel
        ])
          .then(([deviceData, deviceTypes]) => {
            return (deviceData?.devices || [])
              .filter(d => {
                let type = deviceTypes.find(t => t.id === d.type);
                if (!type) { // exclude devices without type
                  return false;
                }

                // Exclude "nonOrganization" device types (device types that was "hidden" by server)
                // SEE comments in CC-753
                return !(type.attributes && type.attributes.some(a => a.name === 'nonOrganization' && a.value === '1'));
              });
          });
      }, [
        this.locationIdResource
      ]);

      /**
       * Location devices behaviors (goals)
       * @type {Resource}
       */
      locationDeviceBehaviorsResource = new Resource(() => {
        // TODO should we check for "rules_engine" microservice in BotsSummary first?
        return this.behaviorsLocationStateResource.value || {};
      }, [
        this.locationIdResource,
        this.behaviorsLocationStateResource
      ]);

      /**
       * List of bot instances (apps) for current location. It's a list of actually running bots
       * @type {Resource}
       */
      locationBotInstancesResource = new Resource(() => {
        return BotsService.getLocationBots(this.locationIdResource.value)
          .then(data => data?.bots || []);
      }, [
        this.locationIdResource
      ]);

      /**
       * Check if there are at least one bot is available for adding to the current location
       * @type {Resource}
       */
      isBotsAvailableForLocationResource = new Resource(() => {
        return BotsService.searchLocationBots(this.locationIdResource.value, {
          limit: 1
        }).then(data => {
          return !!data && !!data.apps && data.apps.length > 0;
        });
      }, [
        this.locationIdResource
      ]);

      locationRulesResource = new Resource(() => {
        return RulesService.getLocationRules(this.locationIdResource.value)
          .then((data) => data?.rules || []);
      }, [
        this.locationIdResource
      ]);

      /**
       * List of location subscriptions
       * @type {Resource}
       */
      locationSubscriptionsResource = new Resource(() => {
        return SubscriptionsService.getLocationSubscriptions(this.locationIdResource.value)
          .then(data => data?.subscriptions || []);
      }, [
        this.locationIdResource
      ]);

      /**
       * List of free (organizational) service plans, available for current location in location Organization
       * @type {Resource}
       */
      locationAvailableFreeServicePlansResource = new Resource(() => {
        return SubscriptionsService.getAllServicePlans(
          this.locationIdResource.value,
          undefined,
          this.locationOrganizationIdResource.value,
          undefined,
          false)
          .then((data) => {
            return data?.servicePlans || [];
          });
      }, [
        this.locationIdResource,
        this.locationOrganizationIdResource
      ]);

      /**
       * List of paid service plans, available for current location by current organization brand.
       * @type {Resource}
       */
      locationAvailablePaidServicePlansResource = new Resource(() => {
        let brandName = utils.getBrandName(this.locationOrganizationIdResource.value, this.availableOrganizationsResource.value);

        // Check for permission = 5 (paid service admin) and 8 (plan upgrade/downgrade ability), only that kid of admin
        //   may assign any available plan.
        // In all other situations we just getting Organization-based plans by organizationId.
        if (!brandName || !this.isPaidAdmin) {
          return [];
        }

        return SubscriptionsService.getAllServicePlans(
          this.locationIdResource.value,
          undefined,
          undefined,
          brandName,
          false)
          .then(paidPlansData => paidPlansData?.servicePlans || []);
      }, [
        this.locationIdResource,
        this.locationOrganizationIdResource,
        this.availableOrganizationsResource,
      ]);

      locationCallCenterResource = new Resource(() => {
        const CALL_CENTER_SERVICES = config.getConfigByKey('callCenter');
        let callCenterServices = this.locationResource.value.services?.filter(service => {
          return CALL_CENTER_SERVICES.some(el => service['name'] && service['name'].includes(el));
        });

        // Get Call Center API only if location had appropriate services
        if (callCenterServices && callCenterServices.length > 0) {
          return MonitoringService.getCallCenter(this.locationIdResource.value)
            .then(data => data?.callCenter);
        } else {
          return $q.resolve(undefined);
        }
      }, [
        this.locationIdResource,
        this.locationResource
      ]);

      /**
       * Billing system info
       * @type {Resource}
       */
      locationBillingSystemResource = new Resource(() => {
        if (this.locationOrganizationPropertiesResource.value) {
          return utils.generateBillingPath(this.locationOrganizationPropertiesResource.value);
        }
      }, [
        this.locationOrganizationPropertiesResource
      ]);

      /**
       * Billing merchant identifier
       */
      locationBillingMerchantResource = new Resource(() => {
        if (this.locationOrganizationPropertiesResource.value) {
          const billingMerchant = this.locationOrganizationPropertiesResource.value.find((m => m.name === 'billingMerchant'));
          return billingMerchant && billingMerchant.value;
        }
      }, [
        this.locationOrganizationPropertiesResource
      ]);

      /**
       * True of we should work with payments or subscriptions in SANDBOX mode
       * @type {Resource}
       */
      isSandboxPaymentResource = new Resource(() => {
        return CloudConfigService.getBaseUrl()
          .then((url) => ServersConfig.billing.hosts?.some(host => !url.toLowerCase().includes(host.toLowerCase())));
      });

      /**
       * Location Questions (services)
       * @type {Resource}
       */
      locationQuestionsResource = new Resource(() => {
        const answerStatuses = config.getConfigByKey('questionAnswerStatuses')
          .filter(el => !el.hidden) // All except "obsolete" and "replaced"
          .map((el) => el.id);

        return QuestionsService.getLocationQuestions(this.locationIdResource.value, { answerStatus: answerStatuses })
          .then((data) => data?.questions || []);
      }, [
        this.locationIdResource
      ]);

      /**
       * Location state "services" (used to modify Questions(Services) on Services tab)
       * @type {Resource}
       */
      locationServicesStateResource = new Resource(() => {
        let stateValue = this.servicesLocationStateResource.value;
        if (stateValue && stateValue.cards && stateValue.cards.length > 0) {
          // Flatten cards array from location state
          return stateValue.cards.flatMap(card => card.content || []);
        } else {
          return [];
        }
      }, [
        this.locationIdResource,
        this.servicesLocationStateResource
      ]);

      /**
       * Location Dashboard Header location state
       * @type {Resource}
       */
      locationHeaderResource = new Resource(() => {
        let headerMicroserviceName = 'dashboard';
        let isHeaderMicroservicePresent = !!this.locationBotSummaryResource.value?.microServices?.includes(headerMicroserviceName);
        if (!isHeaderMicroservicePresent) {
          return $q.resolve(undefined);
        }
        return this.dashboardHeaderLocationStateResource.init();
      }, [
        this.locationBotSummaryResource,
        this.locationIdResource
      ]);

      /**
       * Location Cards
       * @type {Resource}
       */
      locationCardsResource = new Resource(() => {
        let cardsMicroserviceName = 'dashboard'; // Microservice required for Cards UI
        let isCardsMicroservicePresent = !!this.locationBotSummaryResource.value?.microServices?.includes(cardsMicroserviceName);
        if (!isCardsMicroservicePresent) {
          return $q.resolve([]);
        }

        let allowedTypes = [0]; // Allowed card types

        return this.nowLocationStateResource.init()
          .then(locationState => {
            let cards = locationState?.cards || [];
            cards = cards.filter(c => c.type != null && allowedTypes.includes(c.type));
            return cards;
          });
      }, [
        this.locationBotSummaryResource,
        this.locationIdResource
      ]);

      /**
       * Location occupancy summary
       */
      locationOccupancyResource = new Resource(() => {
        let occupancyMicroserviceName = 'occupancy'; // Microservice required for Occupancy UI
        let isOccupancyMicroservicePresent = !!this.locationBotSummaryResource.value?.microServices?.includes(occupancyMicroserviceName);
        if (!isOccupancyMicroservicePresent) {
          return $q.resolve(undefined);
        }

        return this.occupancyLocationStateResource.init()
          .then(locationState => {
            return $q.resolve(locationState);
          });
      }, [
        this.locationBotSummaryResource,
        this.locationIdResource
      ]);

      /**
       * Location Trends (merged with Time States)
       * @type {Resource}
       */
      locationTrendsResource = new Resource(() => {
        let trendsMicroserviceName = 'trends'; // Microservice required for Trends UI
        let isTrendsMicroservicePresent = !!this.locationBotSummaryResource.value?.microServices?.includes(trendsMicroserviceName);
        if (!isTrendsMicroservicePresent) {
          return $q.resolve([]);
        }

        return $q.all([
          this.weeklyLocationTrendsResource.init(),
          this.trendsCategoryLocationStateResource.init(),
          this.trendsLocationStateResource.init()
        ])
          .then(([weeklyLocationTrends, trendsCategoryLocationState, trendsLocationState]) => {
            const categories = trendsCategoryLocationState || {};

            // Add default 'other' category
            if (!categories['category.other']) {
              categories['category.other'] = {
                title: $t('locations.edit.trends.other.title'),
                description: $t('locations.edit.trends.other.desc')
              };
            }
            let otherCategory = categories['category.other'];

            for (const categoryId in categories) {
              const category = categories[categoryId];
              category.trends = [];
              category.id = categoryId;
            }

            // Put every trend into related category (one per trend)
            for (const trendId in (trendsLocationState || {})) {
              if (!Object.hasOwnProperty.call(trendsLocationState, trendId)) {
                continue;
              }

              const trend = trendsLocationState[trendId];
              trend.id = trendId;

              // Add weekly time-series data to related trend
              trend.weeklyData = weeklyLocationTrends?.[trendId] || [];

              // Latest available zscore in weekly data
              trend.zscore = trend.weeklyData.length > 0
                ? trend.weeklyData.slice().reverse().find(e => e.zscore != null)?.zscore
                : undefined;

              // Latest available avg in weekly data
              trend.avg = trend.weeklyData.length > 0
                ? trend.weeklyData.slice().reverse().find(e => e.avg != null)?.avg
                : undefined;

              // Check if trend has category attribute and this category exists
              const trendCategory = categories[trend.category];
              if (!!trend.category && !!trendCategory) {
                // Set flag for category with primary trend
                if (trendCategory.primary_trend === trendId) {
                  trendCategory.primary = true;
                  trend.primary = true;
                }
                trendCategory.trends.push(trend);
              } else { // No category listed in 'trends_category'
                otherCategory.trends.push(trend);
              }

            }

            // Convert categories into Array
            return Object.values(categories)
              .filter(category => category.trends?.length > 0)
              .map(category => {
                category.trends = orderBy(category.trends, ['primary', 'id']);
                return category;
              });
          });
      }, [
        this.locationBotSummaryResource,
        this.locationIdResource
      ]);

      /**
       * Location Time States (for Trends UI)
       * @type {Resource}
       */
      weeklyLocationTrendsResource = new Resource(() => {
        let startDate = moment()
          .subtract(7, 'd')
          .valueOf();
        let now = new Date().getTime();
        let trendsStateName = 'trends';

        return LocationsService.getLocationTimeState(this.locationIdResource.value, trendsStateName, startDate, now)
          .then(data => {
            const weeklyTrendsData = {};

            data?.states
              ?.forEach(timeState => {
                for (const [trendName, trendRecord] of Object.entries(timeState?.value || {})) {
                  if (!weeklyTrendsData[trendName]) {
                    weeklyTrendsData[trendName] = [];
                  }
                  weeklyTrendsData[trendName].push({
                    ...trendRecord,
                    stateDate: timeState.stateDate,
                    stateDateMs: timeState.stateDateMs,
                  });
                }
              });

            return weeklyTrendsData;
          });
      }, [
        this.locationTimeZoneIdResource,
        this.locationIdResource
      ]);

      // #region Call Center Resources

      /**
       * Location Call Center Service (for Call Center UI)
       * @type {Resource}
       */
      callCenterServiceResource = new Resource(() => {
        let callCenterMicroserviceName = 'ecc'; // Microservice required for Call Center service
        let isCallCenterMicroservicePresent = !!this.locationBotSummaryResource.value?.microServices?.includes(callCenterMicroserviceName);

        if (!isCallCenterMicroservicePresent) {
          return $q.resolve();
        }

        return this.locationServicesStateResource.init()
          .then(servicesLocationState => {
            let callCenterService = !!servicesLocationState.length > 0 && servicesLocationState.find(s => s.id.startsWith('ecc.'));
            return $q.resolve(callCenterService);
          }, err => $q.resolve());
      }, [
        this.locationBotSummaryResource,
        this.locationIdResource
      ]);

      // #endregion

      // #endregion

      /**
       * Current (active) tab
       * @type {'dashboard'|'info'|'subscriptions'|'users'|'devices'|'events'|'services'|'scenes'|'bots'|'rules'|'settings'|'states'|'tasks'|'priorities'}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab
       * @return {'dashboard'|'info'|'subscriptions'|'users'|'devices'|'events'|'services'|'scenes'|'bots'|'rules'|'settings'|'states'|'tasks'|'priorities'|undefined}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      /**
       * Task to edit
       */
      taskToEdit;

      /**
       * Force set search value for rules tab
       */
      rulesTabSearchValue;

      /**
       * Selected trend ID to show on Trends History tab
       * Selected trend category ID from the categories list
       */
      selectedHistoryTrend;
      selectedTrendCategory;

      isUnlimitedAdmin = PermissionsService.isUnlimitedAdmin();

      isPaidAdmin = PermissionsService.isPaidAdmin();

      isServiceAdmin = PermissionsService.isServiceAdmin();

      // #endregion

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      // #endregion

      // #region Public methods

      /**
       * Universal handle function for Cancel or Back button
       */
      goBack() {
        if ($state.params && $state.params.from) {
          switch ($state.params.from) {
            case 'user':
              $state.go('main.users.edit', {
                tab: 'locations'
              });
              return;
            case 'device':
              $state.go('main.devices.edit', {
                tab: 'location'
              });
              return;
            case 'devices':
              $state.go('main.devices.list');
              return;
            case 'notification':
              $state.go('main.notifications.info', {
                tab: 'relations'
              });
              return;
            case 'notifications':
              $state.go('main.notifications.list');
              return;
          }
        }
        $state.go('main.locations.list');
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }

        // Unsubscribe from WebSocket events
        for (const [stateName, subscription] of Object.entries(this.#wsSubscriptions)) {
          subscription.unsubscribe();
        }

        this.getLocationNarratives.clearEntireCache();
        this.getLocationScenesHistory.clearEntireCache();
        this.getLocationPriorityHistory.clearEntireCache();
        this.getLocationOccupancyHistory.clearEntireCache();
      }

      /**
       * Save location task object
       * @param task
       */
      saveLocationTask(task) {
        let locationId = parseInt(this.locationIdResource.value);
        return BotsService.sendDataStreamMessage({
            locations: [
              locationId
            ],
            feed: task
          },
          {
            scope: 1,
            address: 'update_task',
            locationId: locationId
          })
          .then((data) => {
            if (this.locationTasksResource.value) {
              let taskIndex = this.locationTasksResource.value
                .findIndex((t) => t.id === task.id);
              if (taskIndex !== -1) {
                this.locationTasksResource.value[taskIndex] = task;
              } else {
                this.locationTasksResource.value.push(task);
              }
            }
            return data;
          });
      }

      /**
       * Remove location task object
       * @param {{id: string, title: string, ...}} task
       */
      removeLocationTask(task) {
        return BotsService.sendDataStreamMessage({
            feed: {
              id: task.id,
              deleted_by: this.currentUserResource.value.user.id
            }
          },
          {
            scope: 1,
            address: 'delete_task',
            locationId: this.locationIdResource.value
          })
          .then(() => {
            this.locationTasksResource.value = this.locationTasksResource.value?.filter(t => t.id !== task.id);
          });
      }

      /**
       * Save location notes
       * @param {string} notes
       * @return {Promise}
       */
      saveLocationNotes(notes = '') {
        this.locationResource.value.notes = notes;
        let params = {
          notes: notes
        };
        return LocationsService.manageSingleLocation(
          this.locationResource.value.organizationId,
          this.locationResource.value.id,
          params);
      }

      /**
       * Save location priority
       * @param {number} priority Priority ctegory ID
       * @return {Promise}
       */
      saveLocationPriority(priority) {
        this.locationResource.value.priorityCategory = priority;
        let params = {
          location: {
            priorityCategory: priority
          }
        };
        return LocationsService.updateLocation(
          this.locationResource.value.organizationId,
          this.locationResource.value.id,
          params)
          .then(() => this.getLocationPriorityHistory.clearEntireCache());
      }

      /**
       * Apply Emergency Header action
       * @param {object} action object from `.resolution.response_options[]` array from 'dashboard_header' location state
       */
      applyEmergencyHeaderAction(action) {
        return $q.all([
          this.currentUserResource.init(),
          this.locationIdResource.init(),
        ])
          .then(() => {
            const resolutionContent = this.locationHeaderResource.value.resolution.content || {};
            return BotsService.sendDataStreamMessage({
                locations: [
                  this.locationIdResource.value
                ],
                feed: {
                  ...resolutionContent,
                  user_id: this.currentUserResource.value?.user?.id,
                  answer: action.content.answer
                }
              },
              {
                scope: 1,
                address: this.locationHeaderResource.value.resolution.datastream_address,
                locationId: this.locationIdResource.value
              });
          });
      }

      saveLocationInfo(location) {
        return LocationsService.updateLocation(
          this.locationResource.value.organizationId,
          this.locationResource.value.id,
          location)

          .then(() => {
            let originalLocation = this.locationResource.value;
            let newLocation = location.location;

            // Update original location object with updated data
            let complexFields = ['country', 'state', 'timezone', 'size'];
            for (let [key, value] of Object.entries(newLocation)) {
              if (!complexFields.includes(key)) {
                originalLocation[key] = value;
              }
            }

            // Re-init country from country ID
            if (newLocation.hasOwnProperty('country')) {
              if (newLocation.country && newLocation.country.id) {
                originalLocation.country = this.countriesResource.value
                  .find(c => c.id === newLocation.country.id);
              } else {
                originalLocation.country = undefined;
              }
            }

            // Re-init state from state ID
            if (newLocation.hasOwnProperty('state')) {
              if (originalLocation.country && originalLocation.country.states && newLocation.state && newLocation.state.id) {
                originalLocation.state = originalLocation.country.states
                  .find(s => s.id === newLocation.state.id);
              } else {
                originalLocation.state = undefined;
              }
            }

            // Re-init timezone from id
            if (newLocation.hasOwnProperty('timezone')) {
              if (newLocation.timezone && newLocation.timezone.id) {
                originalLocation.timezone =
                  originalLocation.country?.timezones?.find(t => t.id === originalLocation.timezone.id)
                  || originalLocation.timezone // original timezone
                  || newLocation.timezone;
              } else {
                originalLocation.timezone = undefined;
              }
            }

            // Re-init location size
            if (newLocation.hasOwnProperty('size')) {
              originalLocation.size = {
                value: newLocation.size.content,
                unit: newLocation.size.unit
              };
            }

            return $q.all([
              this.locationTimeZoneIdResource.init(true),
              this.locationTimeZoneOffsetResource.init(true)
            ]);
          });
      }

      addLocationGroups(groupTags) {
        if (!groupTags || !groupTags.length === 0) {
          return $q.resolve();
        }

        let tags = groupTags.map(tag => ({
          id: this.locationIdResource.value,
          tag: tag,
          type: 2, // Location tag
          category: 2, // Grouping tag
        }));

        return TagsService.saveTags(this.locationOrganizationIdResource.value, tags)
          .then(() => {
            return $q.all([
              this.locationResource.init(true),
              this.availableGroupsResource.init(true),
              this.locationGroupsResource.init(true),
            ]);
          });
      }

      removeLocationGroup(groupTag) {
        if (!groupTag) {
          return $q.resolve();
        }

        let tagType = 2; // Location tag

        return TagsService.deleteTags(this.locationOrganizationIdResource.value, tagType, this.locationIdResource.value, groupTag)
          .then(() => {
            return $q.all([
              this.locationResource.init(true),
              this.availableGroupsResource.init(true),
              this.locationGroupsResource.init(true),
            ]);
          });
      }

      /**
       * Remove user from location
       * @param {number} userId
       * @return {Promise}
       */
      removeLocationUser(userId) {
        return LocationsService.removeLocationUser(this.locationIdResource.value, userId)
          .then(() => {
            this.locationUsersResource.value = this.locationUsersResource.value?.filter(u => u.id !== userId);

            return $q.all([
              this.locationAdminUsersResource.init(true),
              this.locationOtherUsersResource.init(true)
            ]);
          });
      }

      /**
       * Permanently delete specified user
       * @param {number} userId User ID to delete
       * @returns {Promise}
       */
      deleteUser(userId) {
        return UsersService.deleteUser(userId);
      }

      /**
       * Permanently delete location
       * @returns {Promise}
       */
      deleteLocation() {
        return LocationsService.deleteLocation(this.locationOrganizationIdResource.value, this.locationIdResource.value);
      }

      /**
       * Search users that are applicable to join the location
       * @param {string} searchTerm search term
       * @param {number} limit how much users we need
       * @return {Promise}
       */
      searchUsersForLocation(searchTerm, limit, originalLimit) {
        if (!limit || limit <= 0) {
          limit = 10;
        }
        if (!originalLimit) {
          originalLimit = limit;
        }

        // FIXME: How it works now: we get N users from server and then filter users that are already on location.
        //  So we can filter all users returned from server and return 0 results (but server may have more users).
        //  The only way to fix it properly - add server parameter "Exclude Location Id". The temp fix is below.
        return this.locationUsersResource.init() // we need this resource initialized
          .then(() => UsersService.getUsersByParams(null, { searchBy: searchTerm + '*', limit: limit * 2 }))
          .then((data) => {
            let users = (data?.users || []);
            let usersFiltered = users
              .filter(u => !this.locationUsersResource.value.some(lu => lu.id === u.id))
              // .filter(u => PermissionsService.canIEditUser(u))
              .slice(0, limit);

            // NOTE: temporary fix for bug described above
            if (usersFiltered.length < limit && users.length === limit * 2) {
              // Search in 2 times more users and try again
              return this.searchUsersForLocation(searchTerm, limit * 2, originalLimit);
            }

            if (users.length > 0 && usersFiltered.length === 0) {
              usersFiltered.usersExists = true;
            }

            return usersFiltered;
          });
      }

      /**
       * Update single user access at current location
       * @param {number} userId User ID to update
       * @param {number} [access] Location access level
       * @param {number} [category] Location notification category
       * @param {boolean} [temporary] Temporary access
       * @param {number} [locationRole] Location user role
       * @return {Promise}
       */
      updateLocationUserAccess(userId, access, category, temporary, locationRole) {
        return LocationsService.updateLocationUser(
          this.locationIdResource.value,
          userId,
          access,
          category,
          !!temporary,
          locationRole
        )
          .then(() => {
            let user = this.locationUsersResource.value.find(u => u.id === userId);
            if (!user) {
              return;
            }
            user.category = category || 0;
            user.locationAccess = access;
            user.temporary = !!temporary;
            user.role = locationRole;
            return $q.all([
              this.locationUsersResource.init(true),
              this.locationAdminUsersResource.init(true),
              this.locationOtherUsersResource.init(true)
            ]);
          });
      }

      /**
       * Update location users call order
       * @param {Array} users
       * @return {Promise}
       */
      updateLocationUsersOrder(users) {
        if (!users || !users.length) {
          return $q.resolve();
        }

        return $q.all(users.map((user, index) => {
          return LocationsService.updateLocationUser(
            this.locationIdResource.value,
            user.id,
            user.locationAccess,
            user.category,
            void 0,
            user.role,
            index + 1,
          );
        }))
          .then(() => {
            return this.locationUsersResource.init(true);
          });
      }

      /**
       * Add user to location
       * @param {Object} user
       * @return {Promise}
       */
      addUserToLocation(user) {
        return LocationsService.addLocationUser(this.locationIdResource.value, user.id)
          .then(() => {
            user = angular.copy(user);
            user.locationAccess = 30;
            user.category = 0;
            user.roleId = user.role?.id || user.roleId;
            this.locationUsersResource.value.push(user);
            return $q.all([
              this.locationUsersResource.init(true),
              this.locationAdminUsersResource.init(true),
              this.locationOtherUsersResource.init(true)
            ]);
          });
      }

      /**
       * Remove device from location
       * @param device
       * @return {Promise}
       */
      removeLocationDevice(device) {
        return DevicesService.deleteDevice(this.locationIdResource.value, device.id)
          .then((data) => {
            this.locationDevicesResource.value = this.locationDevicesResource.value.filter(d => d.id !== device.id);
          });
      }

      /**
       * Set location bot instance status
       * @param {Object} bot
       * @param {number} newStatus new status. 1 for Activated, 2 for deactivated
       * @return {Promise}
       */
      setLocationBotStatus(bot, newStatus) {
        BotsService.getBotSummary.clearCache(this.locationIdResource.value);
        return BotsService.setBotStatus(bot.appInstanceId, newStatus)
          .then(() => {
            let botToChange = this.locationBotInstancesResource.value?.find(b => b.appInstanceId === bot.appInstanceId);
            if (botToChange) {
              botToChange.status = newStatus;
            }
            this.locationBotSummaryResource.dropCacheRecursively();
          });
      }

      /**
       * Remove bot instance from location
       * @param bot
       * @return {Promise}
       */
      removeLocationBotInstance(bot) {
        BotsService.getBotSummary.clearCache(this.locationIdResource.value);

        return BotsService.deleteBotInstance(bot.appInstanceId)
          .then(() => {
            this.locationBotInstancesResource.value = this.locationBotInstancesResource.value
              ?.filter(b => b.appInstanceId !== bot.appInstanceId);
            this.isBotsAvailableForLocationResource.init(true); // we don't need to wait until this request will complete
            this.locationBotSummaryResource.dropCacheRecursively();
          });
      }

      /**
       * Get location narratives (events). Result is cached forever.
       * @param params
       * @type {function}
       * @return {Promise}
       */
      getLocationNarratives = CacheService.cachePromiseFunc((params) => {
        return LocationsService.getNarratives(this.locationIdResource.value, params);
      });

      /**
       * Get location scenes history. Result is cached forever.
       * @param startDate
       * @param endDate
       * @type {function}
       * @return {Promise}
       */
      getLocationScenesHistory = CacheService.cachePromiseFunc((startDate, endDate) => {
        return LocationsService.getLocationScenesHistory(this.locationIdResource.value, startDate, endDate);
      });

      /**
       * Get location occupancy history. Result is cached forever.
       * @param startDate
       * @param endDate
       * @type {function}
       * @returns {Promise}
       */
      getLocationOccupancyHistory = CacheService.cachePromiseFunc((startDate, endDate) => {
        return LocationsService.getLocationTimeState(this.locationIdResource.value, 'occupancy', startDate, endDate);
      });

      /**
       * Get location priorities history. Result is cached forever.
       * @param params
       * @type {function}
       * @return {Promise}
       */
      getLocationPriorityHistory = CacheService.cachePromiseFunc((params) => {
        return LocationsService.getLocationPriorityHistory(this.locationIdResource.value, params);
      });

      /**
       * Remove rule from location
       * @param rule
       * @return {Promise}
       */
      removeLocationRule(rule) {
        return RulesService.deleteRule(rule.id, this.locationIdResource.value)
          .then(() => {
            this.locationRulesResource.value = this.locationRulesResource.value.filter(r => r.id !== rule.id);
          });
      }

      /**
       * Grant free subscription to the location
       * @param {{id: number, organizationId?: number}} plan
       * @param {date} endDate
       * @return {Promise}
       */
      grantSubscription(plan, endDate) {
        let services = [
          {
            locationId: this.locationIdResource.value
          }
        ];

        // Always use Organization ID for Organization-based (free) plans
        let organizationId = this.locationAvailableFreeServicePlansResource?.value?.some(p => p.id === plan.id)
          ? this.locationOrganizationIdResource.value
          : undefined;
        let endDateStr = endDate?.toISOString();

        return SubscriptionsService.grantSubscriptionToLocations(plan.id, services, organizationId, endDateStr)
          .then((data) => {
            if (data?.results && data.results[0]?.resultCode !== 0) {
              return $q.reject({
                resultCode: data.results[0]?.resultCode,
                resultCodeDesc: data.results[0]?.resultCodeMessage || data.results[0]?.resultCodeDesc || $t('users.messages.subscription-assign-error')
              });
            }

            this.locationResource.dropCacheRecursively();
            this.locationSubscriptionsResource.dropCacheRecursively();
            this.locationAvailableFreeServicePlansResource.dropCacheRecursively();
            this.locationAvailablePaidServicePlansResource.dropCacheRecursively();
            this.locationCallCenterResource.dropCacheRecursively();

            // Clear bots cache cause subscription change may change bots list
            BotsService.getBotSummary.clearCache(this.locationIdResource.value);
            this.locationBotSummaryResource.dropCacheRecursively();
            this.locationBotInstancesResource.dropCacheRecursively();
            this.isBotsAvailableForLocationResource.dropCacheRecursively();

            return $q.all([
              this.locationResource.init(true),
              this.locationSubscriptionsResource.init(true),
              this.locationAvailableFreeServicePlansResource.init(true),
              this.locationAvailablePaidServicePlansResource.init(true),
              this.locationCallCenterResource.init(true)
            ]);
          });
      }

      /**
       * Grant paid subscription to the location
       * @param {{id: number, subscriptionId: number, ...}} plan
       * @param {{id: number, ...}} price
       * @param {{id: number, ...}} user
       * @param {string} paymentProviderSubscriptionId
       * @param {string} [site]
       * @return {Promise}
       */
      grantSubscriptionByPaymentProviderSubscriptionId(plan,
                                                       price,
                                                       user,
                                                       paymentProviderSubscriptionId,
                                                       site) {

        if (!paymentProviderSubscriptionId || !price || !user) {
          return $q.reject({
            resultCode: 8,
            resultCodeDesc: 'users.messages.subscription-assign-error'
          });
        }

        let isSandbox = !!this.isSandboxPaymentResource.value;
        let locationId = this.locationIdResource.value;

        // Create new paid subscription and link to existing Payment Provider recurring
        return SubscriptionsService.purchaseSubscription(price, locationId, user.id, paymentProviderSubscriptionId, isSandbox, site)
          .then(() => {
            this.locationResource.dropCacheRecursively();
            this.locationSubscriptionsResource.dropCacheRecursively();
            this.locationAvailableFreeServicePlansResource.dropCacheRecursively();
            this.locationAvailablePaidServicePlansResource.dropCacheRecursively();
            this.locationCallCenterResource.dropCacheRecursively();

            // Clear bots cache cause subscription change may change bots list
            BotsService.getBotSummary.clearCache(this.locationIdResource.value);
            this.locationBotSummaryResource.dropCacheRecursively();
            this.locationBotInstancesResource.dropCacheRecursively();
            this.isBotsAvailableForLocationResource.dropCacheRecursively();

            return $q.all([
              this.locationResource.init(true),
              this.locationSubscriptionsResource.init(true),
              this.locationAvailableFreeServicePlansResource.init(true),
              this.locationAvailablePaidServicePlansResource.init(true),
              this.locationCallCenterResource.init(true)
            ]);
          });
      }

      revokeSubscription(subscription) {
        return SubscriptionsService.cancelSubscription(subscription.userPlanId, this.locationIdResource.value)
          .then(() => {
            this.locationResource.dropCacheRecursively();
            this.locationSubscriptionsResource.dropCacheRecursively();
            this.locationAvailableFreeServicePlansResource.dropCacheRecursively();
            this.locationAvailablePaidServicePlansResource.dropCacheRecursively();
            this.locationCallCenterResource.dropCacheRecursively();

            // Clear bots cache cause subscription change may change bots list
            BotsService.getBotSummary.clearCache(this.locationIdResource.value);
            this.locationBotSummaryResource.dropCacheRecursively();
            this.locationBotInstancesResource.dropCacheRecursively();
            this.isBotsAvailableForLocationResource.dropCacheRecursively();

            return $q.all([
              this.locationResource.init(true),
              this.locationSubscriptionsResource.init(true),
              this.locationAvailableFreeServicePlansResource.init(true),
              this.locationAvailablePaidServicePlansResource.init(true),
              this.locationCallCenterResource.init(true)
            ]);
          });
      }

      /**
       * Update question answer
       * @param {{id: number, answer: number|string|boolean}} question
       * @return {Promise}
       */
      updateQuestionAnswer(question) {
        if (question.responseType === 2 && question.answer != null) {
          // For questions with options
          question.answer = parseInt(question.answer);
        } else if (question.responseType === 1 && question.displayType === 2) {
          // For single-toggle questions
          question.answer = true;
        }

        return QuestionsService.answerSingleQuestion(this.locationIdResource.value, question.id, question.answer)
          .then((data) => {
            let newQuestionInfo = data.questions[0];
            let questionToUpdate = this.locationQuestionsResource.value?.find(q => q.id === question.id);
            if (questionToUpdate && newQuestionInfo) {
              questionToUpdate.answer = newQuestionInfo.answer;

              // NOTE: `answerStatus` is deprecated, but still used in server responses.
              //  `answerStatus` and `status` fields are the same thing
              //  we should prefer `status` in our app
              questionToUpdate.answerStatus = newQuestionInfo.answerStatus;
              questionToUpdate.status = newQuestionInfo.answerStatus;
              questionToUpdate.answerDate = newQuestionInfo.answerDate;
              questionToUpdate.answerDateMs = newQuestionInfo.answerDateMs;
            }
          });
      }

      /**
       * Get location trends historical data
       * @param {string} trendId
       * @param {'hour'|'day'|'month'|'week'} aggregateBy
       * @param {number} startDateMs
       * @param {number} endDateMs
       * @return {Promise}
       */
      getLocationTrendsHistory(trendId,
                               aggregateBy,
                               startDateMs,
                               endDateMs) {
        let aggregation = TRENDS_HISTORY_AGGREGATIONS[aggregateBy];

        return LocationsService.getLocationTimeStatesHistory(
          this.locationIdResource.value,
          {
            name: 'trends',
            field: trendId,
            //field: [trendId, 'value'],
            //aggregation: aggregation,
            startDate: startDateMs,
            endDate: endDateMs,
          });
      }

      /**
       * Is it Gateway or not to show/hide description.
       * @param {object} device Device object
       * @return {boolean}
       */
      isGateway(device) {
        if (!device || !device.typeCategory) {
          return false;
        }
        return GATEWAY_DEVICE_CATEGORIES.some(cat => cat === device.typeCategory);
      }

      /**
       * Change location scene/mode/event.
       * This action is only available to the Service Admin (with permission 8).
       * @param {string} scene Location scene/mode/event to set.
       * @return {Promise}
       */
      changeLocationScene(scene) {
        if (!PermissionsService.canIChangeLocationScene(this.locationResource.value)) {
          return $q.reject('This action is only available to the Service Administrator');
        }

        return LocationsService.setLocationScene(this.locationIdResource.value, scene)
          .then(() => {
            if (this.locationResource?.value) {
              this.locationResource.value.event = scene;
            }
            this.getLocationScenesHistory.clearEntireCache();
          });
      }

      /**
       * Update location occupancy state.
       * @param {string} occupancy Location occupancy to set.
       * @return {Promise}
       */
      changeLocationOccupancy(occupancy) {
        if (!occupancy || !occupancy.value) {
          return $q.reject('Missing occupancy status');
        }

        return BotsService.sendDataStreamMessage({
            feed: {
              mode: occupancy.value
            }
          },
          {
            scope: 1,
            address: 'set_occupancy_state',
            locationId: this.locationIdResource.value
          })
          .then(() => {
            if (this.locationOccupancyResource?.value) {
              this.locationOccupancyResource.value.status = occupancy.value;
              this.locationOccupancyResource.value.startDate = new Date().getTime();
            }
            this.getLocationOccupancyHistory.clearEntireCache();
          });
      }

      /**
       * Get MMS number for the Apple SOS from system property.
       * TODO: MMS number should be a part of location-user combination just like SMS number.
       */
      getMMSNumber() {
        const MMS_SYSTEM_PROPERTY = 'mms_number';
        return UsersService.getUserOrSystemPropertyValue(MMS_SYSTEM_PROPERTY)
          .then(result => {
            return result || '';
          }, err => {
            return '';
          });
      }

      /**
       * Get location highlights metadata from system property.
       */
      getLocationHighlightsMetadata = CacheService.cachePromiseFunc(() => {
        const HIGHLIGHTS_METADATA_PROPERTY = 'highlights.metadata';
        return UsersService.getUserOrSystemPropertyValue(HIGHLIGHTS_METADATA_PROPERTY)
          .then(result => {
            const highlightsMetadata = result && JSON.parse(result);
            return highlightsMetadata ? highlightsMetadata : {
              highlights: [],
              indicators: [],
            }
          }, err => {
            return undefined;
          });
      });

      /**
       * Search locations by any available attribute.
       * @param {string} searchTerm Search term to find locations.
       * @param {boolean} [globalSearch] If true, search across all available organizations.
       * @param {number} [subType] Location sub-type. 0 - Residential, 1 - Devices only.
       */
      searchLocations(searchTerm, globalSearch = false, subType) {
        const organizationId = globalSearch ? void 0 : this.locationOrganizationIdResource.value;
        const currentLocationId = this.locationIdResource.value;
        return LocationsService.getLocations(organizationId, {
          tree: false,
          getTags: false,
          subType: subType,
          sortBy: 'creationDateMs',
          sortOrder: 'desc',
          searchBy: '*' + searchTerm + '*',
          limit: 10
        })
          .then(result => {
            return (result?.locations || [])
              .filter(l => l.id !== currentLocationId); // remove current location
          });
      }

      cloneLocationDevice(device, newLocation) {
        const currentLocationId = this.locationIdResource.value;

        return DevicesService.simulatedDeviceCopy(
          currentLocationId,
          device.id,
          newLocation ? { simulatedLocationId: newLocation.id } : undefined
        )
          .then(() => {
            return this.locationDevicesResource.init(true);
          });
      }

      addRelativeLocation(location) {
        if (!location || !location.id) {
          return $q.reject('Invalid location to add as relative');
        }
        if (this.locationIdResource.value.subType === location.subType) {
          return $q.reject('Can add only location with different sub-type as relative');
        }

        return LocationsService.addSubLocation(this.locationIdResource.value, location.id)
          .then(() => {
            return this.locationResource.init(true);
          });
      }

      removeRelativeLocation(location) {
        if (!location || !location.id) {
          return $q.reject('Invalid location to remove from relatives');
        }

        return LocationsService.removeSubLocation(this.locationIdResource.value, location.id)
          .then(() => {
            return this.locationResource.init(true);
          });
      }

      // #endregion

      // #region Private methods

      // #region Small hack to avoid multiple calls to single getLocationState API

      /**
       * Queue for debounced Get Location State function
       * @type {Array<{stateName: string, deferred: Deferred}>}
       */
      #getLocationStatesQueue = [];

      /**
       * WebSocket subscriptions
       */
      #wsSubscriptions = {};

      /**
       * Get Location State (debounced function)
       * @param {string} stateName
       * @return {Promise<{value: Object}>}
       */
      #getLocationState(stateName) {
        // debounce and couple requests
        let deferred = $q.defer();
        this.#getLocationStatesQueue.push({ stateName: stateName, deferred: deferred });
        this.#getLocationStateDebounced();
        return deferred.promise;
      }

      /**
       * Subscribe for location state changes
       * @param {string} stateName
       * @param {function} onChange
       */
      #subscribeForLocationStateChanges(stateName, onChange) {
        let subscription;
        if (this.#wsSubscriptions[stateName]) {
          subscription = this.#wsSubscriptions[stateName];
        } else {
          subscription = LocationsService.subscribeForLocationStates(this.locationIdResource.value, stateName);
        }

        subscription.onCreate((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data?.locationState?.value);
          });
        });

        subscription.onEdit((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data?.locationState?.value);
          });
        });

        subscription.onDelete((data) => {
          $rootScope.$applyAsync(() => {
            onChange(undefined);
          });
        });

        subscription.onError((data) => {
          $log.warn(`Location state '${stateName}' subscription error`);
          $log.warn(data);
        });

        this.#wsSubscriptions[stateName] = subscription;

        return subscription;
      }

      #getLocationStateDebounced = utils.debounce(() => {
        let queue = this.#getLocationStatesQueue;
        this.#getLocationStatesQueue = [];
        let locationStates = queue.map(q => q.stateName)
          .filter((value, index, self) => self.indexOf(value) === index); // remove duplicates

        LocationsService.getLocationState(this.locationIdResource.value, locationStates)
          .then(data => {
            let states = data?.states || [];
            queue.forEach(q => {
              let state = states.find(s => s.name === q.stateName);
              if (state) {
                q.deferred.resolve({ value: state.value });
              } else {
                if (data?.value) { // if requested only one state
                  q.deferred.resolve({ value: data?.value });
                } else {
                  q.deferred.resolve({ value: undefined });
                }
              }
            });
          }, (err) => {
            queue.forEach(q => {
              q.deferred.reject(err);
            });
          });
      }, 50);

      // #endregion

      // #endregion

    }

    return EditLocationDataProvider;
  })
  .name;



